<template>
  <div class="modal" :class="{'is-active': active}" @dragover="dragOver" @drop="drop">
    <div class="modal-background" />
    <div class="modal-card" :qa="active ? 'attach-modal' : ''">
      <button @click="close" class="delete modal-close" aria-label="close" qa="close-attach-modal"></button>
      <div class="modal-header title is-uppercase is-left" qa="modal-header">
        <i class="fa" :class="iconForType"/> <span v-if="type!=='iframe' && type!=='poll'">
        {{ $t("projectPage.attach") }} {{ $t(textForType) }}</span>
        <span v-else-if="type=='iframe'">{{ $t("projectPage.attachIframe") }}</span>
        <span v-else-if="type==='poll'">{{ this.shouldShowPollTypeTitle }}</span>
      </div>
      <section class="modal-card-body" v-if="isDragging">
        <div v-if="error" class="notification is-danger">{{ error }}</div>
        <div class="drop-target" :class="{over: isOverDropTarget}" @dragenter="dragEnter" @dragleave="dragLeave">
          {{ $t("projectPage.dropFile") }}
        </div>
      </section>
      <section class="modal-card-body" v-if="!isDragging">
        <div v-if="error" class="notification is-danger" qa="error-msg">{{ error }}
          <button @click="error = ''" class="delete has-text-white"></button>
        </div>
        <div v-if="!attaching" class="control">
          <div class="columns">
            <!-- Show File -->
            <div class="column" v-if="shouldShowFile">
              <div class="columns is-mobile">
                <div class="column file-input-container">
                  <button class="button is-fullwidth" @click="$refs.file.click()" qa="choose-file-button">
                    &nbsp;
                    <span v-if="file">
                    <!-- Desktop view -->
                      <span class="is-hidden-mobile">
                        {{ limitStringLength(file.name, strLimitDesktop) }}
                      </span>

                      <span class="is-hidden-tablet">
                        <!-- Mobile view under 450 width -->
                        <span v-if="viewPortWidth < 450">
                          {{ limitStringLength(file.name, strLimitSmallMobile) }}
                        </span>

                        <!-- Mobile view between 768 and 450 width -->
                        <span v-else-if="isMobile">
                          {{ limitStringLength(file.name, strLimitMobile) }}
                        </span>
                      </span>
                    </span>

                    <span v-else>
                      {{ $t("projectPage.choose") }} {{ $t(textForType) }}
                    </span>
                  </button>
                  <!-- video formats accepted based on information listed on this link https://docs.aws.amazon.com/mediaconvert/latest/ug/reference-codecs-containers-input.html -->
                  <input type="file" id="file" ref="file"
                         :accept="this.acceptedFormats"
                         @change="selectFile"
                  />
                </div>
                <div class="column is-narrow" v-if="file">
                  <button v-if="file" class="button is-danger is-full-width" @click="file = null"><span class="icon"><i
                      class="fal fa-trash"></i></span></button>
                </div>
              </div>
            </div>
            <div class="column" v-if="shouldShowURL">
              <div class="columns is-mobile">
                <div v-if="!showAvatar" class="column">
                  <div class="control has-icons-left">
                    <input ref="linkInput" class="input" type="text" v-model.trim="link" placeholder="URL"
                           @input="changeMode('link')" @keyup.enter="attach" :qa="active ? 'url-input' : ''"/>
                    <span class="icon is-small is-left"><i class="fal fa-link"></i></span>
                  </div>
                </div>
                <div class="column is-narrow" v-if="shouldShowURL && link && !showAvatar">
                  <button v-if="shouldShowURL" class="button is-danger is-full-width" @click="link = ''"><span
                      class="icon"><i class="fal fa-trash"></i></span></button>
                </div>
              </div>
              <div class="column is-narrow avatar-close-button" v-if="shouldShowURL && link && showAvatar">
                <button v-if="shouldShowURL" class="button is-danger is-full-width" @click="closeAvatarMode();link = ''"><span
                    class="icon"><i class="fal fa-trash"></i></span></button>
              </div>
            </div>
            <!-- IFrame -->
            <div class="column" v-if="shouldShowIframeURL">
              <div class="columns is-mobile">
                <div class="column">
                  <div class="control has-icons-left">
                    <input ref="linkFrame" class="input" type="text" v-model.trim="iframe" placeholder="Embed URL"
                           @input="changeIframeMode('iframe')" @keyup.enter="attach" :qa="active ? 'url-input' : ''"/>
                    <span class="icon is-small is-left"><i class="fal fa-browser"></i></span>
                  </div>
                </div>
                <div class="column is-narrow" v-if="shouldShowIframeURL && iframe">
                  <button v-if="shouldShowIframeURL" class="button is-danger is-full-width" @click="iframe = ''"><span
                      class="icon"><i class="fal fa-trash"></i></span></button>
                </div>
              </div>
            </div>
            <!-- Show Poll Information -->
            <div class="column poll" v-if="shouldShowPoll">
              <polls :poll="poll"
                     :pollOptions="pollOptions"
                     :formSelected="formSelected"
                     :pollSelected="pollSelected"
                     :multiSelect="multiSelect"
                     :singleSelect="singleSelect"
                     :textField="textField"
                     :deletePollOption="deletePollOption"
                     :addPollOption="addPollOption"
                     :resetPollSelection="resetPollSelection"
                     :getPollSelectedField="getPollSelectedField"/>
            </div>
          </div>
        </div>
        <h4 v-if="isAdminPage" class="help is-primary">Admins: To upload an image link to our storage, please download
          the image and upload it here. Otherwise you can use the link directly</h4>
        <image-search v-if="shouldShowImageSearch" @selected="selectImage"></image-search>
        <div  v-if="!isProfileImage" class="control">
          <checkbox v-if="shouldShowDownloadable" class="is-size-7 has-text-grey remove-outline" v-model="isDownloadable" :label="$t('projectPage.downloadAttachment')" id="downloadable" name="downloadable" value="isDownloadable"/>
        </div>
        <div class="avatar-container" v-if="!showAvatar && shouldShowAvatarPicker">
          <button class="button" @click="showAvatarMode">
            <span class=""><i class="far fa-grin-stars pr-1 is-size-6"></i>{{ $t("Generate Avatar") }}</span>
          </button>
        </div>
        <avatar-picker v-if="showAvatar" @selectedAvatar="selectImage"></avatar-picker>
        <div v-if="attaching">
          <span v-if="!progress" class="loading-container is-flex"><span
              class="loader"></span>&nbsp;&nbsp;{{ attachingMessage }}...</span>
          <progress v-if="progress" class="progress is-primary is-marginless" :value="progress.percentage" max="100">
            {{ progress.percentage }}%
          </progress>
          <span v-if="progress" class="help is-pulled-right">{{ progress.percentage }}% Complete</span>
        </div>
      </section>
      <div class="button-container is-flex"
           :class="{'button-container_form': singleSelect || multiSelect}">
        <span v-if="singleSelect || multiSelect" style="display: flex">
          <i class="fal fa-check-square button-container_icon" />
          <p class="button-container_text">Team member should have different answers for these questions</p>
        </span>
        <div class="cancel-and-save">
          <div v-if="!attaching"
               @click="close"
               class="button cancel is-primary is-outlined is-uppercase"
               qa="cancel-attach-button"
               tabindex="0"
               @keypress.enter.space="close"> {{ $t("cancel") }}
          </div>
          <!-- Regular button without poll -->
          <div v-if="!shouldShowPoll"
               class="save button is-primary is-uppercase"
               :disabled="attaching"
               @click="attach"
               tabindex="0"
               @keypress.enter.space="attach">
            <span :qa="active ? 'attach-btn' : ''">
              {{ this.shouldShowButtonText }}
            </span>
          </div>
          <div v-if="shouldShowPoll && ((this.pollSelected || this.formSelected) || (this.singleSelect || this.multiSelect || this.textField))"
               class="save button is-primary is-uppercase"
               :disabled="attaching"
               @click="attach"
               tabindex="0"
               @keypress.enter.space="attach">
            <span :qa="active ? 'attach-btn' : ''">
              {{ this.shouldShowButtonText }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageSearch from './ImageSearch'
import AvatarPicker from './AvatarPicker'
import Polls from './Polls'

import upload from '@/api/upload'
import firebase from '@/libs/firebase'
import video from '@/libs/video-type'
import urlLib from '@/libs/url'
import moment from 'moment'
import i18n from '@/i18n'
import dragAndDrop from '@/mixins/drag-and-drop'
import componentState from '@/mixins/component-state'
import breakpoints from '@/libs/breakpoints'
import verge from 'verge'
import Checkbox from '@/components/Controls/Checkbox'

const maxFileSize = 50000000 // 50MB
const maxImageSize = 10000000 // 10MB
const maxVideoSize = 500000000 // 500MB
const maxSizeWarning = i18n.t('attachmentsWarnings.maxFile')
const maxImageSizeWarning = i18n.t('attachmentsWarnings.maxImage')
const maxVideoSizeWarning = i18n.t('attachmentsWarnings.maxVideo')
const imageTypeWarning = i18n.t('attachmentsWarnings.imageSupport')
const videoTypeWarning = i18n.t('attachmentsWarnings.videoSupport')
const imageOrVideoSupport = i18n.t('attachmentsWarnings.imageOrVideoSupport')
const imageSupportedFormats = i18n.t('attachmentsWarnings.imageSupportedFormats')
const videoSupportedFormats = i18n.t('attachmentsWarnings.videoSupportedFormats')
const imageOrVideoTypeWarning = `${imageOrVideoSupport}

${imageSupportedFormats}
${videoSupportedFormats}`
const fileTypeWarning = i18n.t('attachmentsWarnings.fileSupport')
const defaultError = 'Unable to attach'

const modes = { file: 'file', link: 'link', data: 'data', poll: 'poll', iframe: 'iframe', avatarPicker: 'avatarPicker' }

const types = {
  file: 'file',
  link: 'link',
  image: 'image',
  mobileUpload: 'upload',
  embeddedVideo: 'embeddedVideo',
  video: 'video',
  poll: 'poll',
  imageorvideo: 'imageorvideo',
  iframe: 'iframe',
  avatarPicker: 'avatarPicker'
}

export default {
  name: 'attach',
  mixins: [dragAndDrop, componentState],
  components: { ImageSearch, Polls, AvatarPicker, Checkbox },
  props: {
    parentID: [String, Number],
    isAdminPage: String,
    showAvatarPicker: Boolean,
    isProfileImage: Boolean
  },
  data: function () {
    return {
      mode: modes.file,
      type: '',
      file: null,
      imageAndVideoFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm', 'video/x-msvideo'],
      excludeFileTypes: ['application/x-msdownload'],
      videoFileTypes: ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm', 'video/x-msvideo'],
      imageFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
      dataURI: null,
      link: '',
      iframe: '',
      error: '',
      attachment: null,
      attaching: false,
      progress: null,
      poll: {
        question: '',
        selectedType: ''
      },
      pollOptions: [],
      attachingMessage: i18n.t('projectPage.attaching[0]'),
      timerId: null,
      strLimitDesktop: 50,
      strLimitMobile: 35,
      strLimitSmallMobile: 20,
      isDownloadable: false,
      showAvatar: false,
      formSelected: false,
      pollSelected: false,
      multiSelect: false,
      singleSelect: false,
      textField: false,
      newPollOption: {
        option: '',
        tag: '',
        score: 1
      }
    }
  },
  computed: {
    isMobile: function () {
      return breakpoints.isMobile()
    },
    viewPortWidth: function () {
      return verge.viewportW()
    },
    active: function () {
      const visible = this.$store.getters.modalIsVisible('attach')
      const data = this.$store.getters.modalData('attach')
      return visible && data.parentID === this.parentID
    },
    canAttach: function () {
      if (this.attaching) {
        return false
      }
      switch (this.mode) {
        case modes.file:
          return !!this.file
        case modes.link:
          return !!this.link
        case modes.iframe:
          return !!this.iframe
        case modes.data:
          return !!this.dataURI
        case modes.poll:
          return !!this.poll
      }

      return false
    },
    // These are used to control when each input shows as an option
    // The 'type' is set by the attachment buttons on the 'Splash' component
    shouldShowFile: function () {
      return (this.type === types.file || this.type === types.image || this.type === types.video || this.type === types.imageorvideo) && this.link === ''
    },
    shouldShowURL: function () {
      return (this.type === types.image || this.type === types.imageorvideo || this.type === types.link || this.type === types.file) && this.file === null && !this.isAdminPage
    },
    shouldShowIframeURL: function () {
      return this.type === types.iframe && !this.isAdminPage
    },
    shouldShowImageSearch: function () {
      return (this.type === types.image || this.type === types.imageorvideo) && this.link === '' && this.file === null && !this.isAdminPage
    },
    shouldShowAvatarPicker: function () {
      return (this.type === types.image || this.type === types.imageorvideo) && this.shouldShowFile && this.shouldShowURL && !this.isAdminPage && this.showAvatarPicker
    },
    shouldShowPoll: function () {
      return this.type === types.poll
    },
    shouldShowDownloadable: function () {
      return !this.attaching && (this.type === types.image || this.type === types.video || this.type === types.imageorvideo)
    },
    shouldShowPollTypeTitle: function () {
      if (this.shouldShowPoll) {
        if (this.formSelected) {
          return i18n.t('projectPage.formTitle') + ' - Step 1 of 2'
        } else if (this.singleSelect) {
          return i18n.t('projectPage.formTitle') + ' - ' + i18n.t('projectPage.singleSelect')
        } else if (this.multiSelect) {
          return i18n.t('projectPage.formTitle') + ' - ' + i18n.t('projectPage.multiSelect')
        } else if (this.textField) {
          return i18n.t('projectPage.formTitle') + ' - ' + i18n.t('projectPage.textField')
        } else if (this.pollSelected) {
          return i18n.t('projectPage.pollTitle')
        } else {
          return i18n.t('projectPage.poll')
        }
      }
    },
    shouldShowButtonText: function () {
      if (this.shouldShowPoll) {
        if (!this.formSelected && !this.pollSelected && !this.multiSelect && !this.singleSelect && !this.textField) {
          return i18n.t('projectPage.next')
        } else if (this.formSelected && (!this.multiSelect && !this.singleSelect && !this.textField)) {
          return i18n.t('projectPage.prev')
        } else if (!this.formSelected && (this.textField || this.multiSelect || this.singleSelect)) {
          return i18n.t('projectPage.save')
        } else if (this.pollSelected) {
          return i18n.t('projectPage.save')
        }
      } else {
        return i18n.t('projectPage.attach')
      }
    },
    iconForType: function () {
      const style = {}
      let icon = 'fa-paperclip'

      switch (this.type) {
        case types.image:
          icon = 'fa-camera'
          break
        case types.link:
          icon = 'fa-link'
          break
        case types.file:
          icon = 'fa-file'
          break
        case types.video:
          icon = 'fa-video'
          break
        case types.poll:
          icon = 'fa-poll'
          break
        case types.imageorvideo:
          icon = 'fa-photo-video'
          break
        case types.iframe:
          icon = 'fa-browser'
      }

      style[icon] = true

      return style
    },
    textForType: function () {
      let jsonStr = ''
      switch (this.type) {
        case types.image:
          jsonStr = 'projectPage.image'
          break
        case types.link:
          jsonStr = 'projectPage.link'
          break
        case types.file:
          jsonStr = 'projectPage.file'
          break
        case types.poll:
          jsonStr = 'projectPage.poll'
          break
        case types.imageorvideo:
          jsonStr = 'projectPage.photoVideo'
          break
        case types.iframe:
          jsonStr = 'projectPage.iframe'
          break
      }
      return jsonStr
    },
    dateToday: function () {
      var today = new Date()
      const year = today.getFullYear()
      let month = today.getMonth() + 1
      if (month < 10) {
        month = '0' + month
      }
      const day = today.getDate()
      return year + '-' + month + '-' + day
    },
    endDate: function () {
      var today = new Date()
      const year = today.getFullYear()
      let month = today.getMonth() + 1
      if (month < 10) {
        month = '0' + month
      }
      const day = today.getDate()
      return year + 1 + '-' + month + '-' + day
    },
    acceptedFormats: function () {
      return this.type === 'file' ? '' : 'video/mp4,video/mpeg,video/quicktime,video/webm,video/avi,image/*'
    }
  },
  mounted: function () {
    this.isDownloadable = false
  },
  watch: {
    active: function (value) {
      if (value) {
        const data = this.$store.getters.modalData('attach')
        this.show(data.type)
      }
      while (this.pollOptions.length < 2) {
        this.pollOptions.push(Object.assign({}, this.newPollOption))
      }
    },
    shouldShowPoll: function (value) {
      if (value) {
        this.mode = modes.poll
      }
    }
  },
  methods: {
    showAvatarMode: function () {
      this.showAvatar = true
    },
    closeAvatarMode: function () {
      this.showAvatar = false
    },
    resetPollSelection: function () {
      this.formSelected = false
      this.pollSelected = false
      this.multiSelect = false
      this.singleSelect = false
      this.textField = false
      this.newPollOption = {
        option: '',
        tag: '',
        score: 1
      }
      // Reset the polls
      this.pollOptions = []
      while (this.pollOptions.length < 2) {
        this.pollOptions.push(Object.assign({}, this.newPollOption))
      }
      this.poll.selectedType = ''
    },
    getPollSelectedField: function (type = 'poll') {
      switch (type.toLowerCase()) {
        case 'poll':
          this.pollSelected = true
          this.poll.selectedType = 'publicproject'
          break
        case 'form':
          this.pollSelected = false
          this.formSelected = true
          this.poll.selectedType = 'registrationform'
          break
        case 'single select':
          this.singleSelect = true
          this.formSelected = false
          this.poll.selectedType = 'registrationform'
          break
        case 'multi select':
          this.multiSelect = true
          this.formSelected = false
          this.poll.selectedType = 'registrationform'
          break
        case 'text field':
          this.textField = true
          this.formSelected = false
          this.poll.selectedType = 'registrationform'
          break
        default :
          this.formSelected = true
          this.poll.selectedType = 'registrationform'
          break
      }
    },
    limitStringLength: function (value, strSize) {
      if (typeof value === 'string' && value.length > strSize) {
        value = value.slice(0, strSize) + '...'
      }
      return value
    },
    show: function (type) {
      this.type = type
      this._callback('lockScrolling')
    },
    close: function (success) {
      this.reset()
      if (success === true) {
        this.$emit('attached', this.attachment)
      } else {
        this.$emit('cancel')
      }
      this._callback('unlockScrolling', success !== true)
      this.$store.dispatch('hideAttachModal')
    },
    hide: function () {
      this._callback('unlockScrolling', true)
      this.$store.dispatch('hideAttachModal')
    },
    reset: function () {
      this.closeAvatarMode()
      this.mode = modes.file
      this.file = null
      this.link = ''
      this.iframe = ''
      this.type = ''
      this.dataURI = null
      this.error = ''
      this.attaching = false
      this.poll.question = ''
      if (this.$refs.file) {
        this.$refs.file.value = ''
      }
    },
    selectFile: function (event) {
      if (this.$refs.file.value) {
        const file = event.target.files[0]
        this.$refs.file.value = ''
        this.checkFile(file)
      }
    },
    checkFile: function (file) {
      if (this.type === types.imageorvideo) {
        if (file) {
          const videoSelected = this.type === types.imageorvideo && this.isVideo(file.type)
          const imageSelected = this.type === types.imageorvideo && this.isImage(file.type)

          if (!imageSelected && !videoSelected) {
            this.error = imageOrVideoTypeWarning
            this.attaching = false
          } else if ((this.type === types.video && file.size > maxVideoSize) || (videoSelected && file.size > maxVideoSize)) {
            this.error = maxVideoSizeWarning
            this.attaching = false
          } else if (imageSelected && file.size > maxImageSize) {
            this.error = maxImageSizeWarning
            this.attaching = false
          } else if ((this.type === types.image && this.imageFileTypes.indexOf(file.type) === -1) || (imageSelected && this.imageFileTypes.indexOf(file.type) === -1)) {
            this.error = imageTypeWarning
            this.attaching = false
          } else if ((this.type === types.video && this.videoFileTypes.indexOf(file.type) === -1) || (videoSelected && this.videoFileTypes.indexOf(file.type) === -1)) {
            this.error = videoTypeWarning
            this.attaching = false
          } else {
            this.error = ''
            this.file = file
            this.attaching = false
          }
        } else {
          console.error(`Modal Attach:: checkfile has no file - ${this.parentID}`)
        }
      } else {
        if (file) {
          if (file.size > maxFileSize) {
            this.error = maxSizeWarning
            this.attaching = false
          } else if (this.type === types.file && this.excludeFileTypes.indexOf(file.type) !== -1) {
            this.error = fileTypeWarning
            this.attaching = false
          } else {
            this.error = ''
            this.file = file
            this.attaching = false
          }
        } else {
          console.error(`Modal Attach:: checkfile has no file - ${this.parentID}`)
        }
      }
    },
    changeMode: function (type) {
      if (this.mode !== modes.link && this.link.length > 0) {
        this.mode = modes.link
      }
      if (type === 'link') {
        this.$nextTick(() => {
          this.$refs.linkInput.focus()
        })
      }
    },
    changeIframeMode: function (type) {
      if (this.mode !== modes.iframe && this.iframe.length > 0) {
        this.mode = modes.iframe
      }
      if (type === 'iframe') {
        this.$nextTick(() => {
          this.$refs.linkFrame.focus()
        })
      }
    },
    changeAvatarPickerMode: function () {
      if (this.mode !== modes.avatarPicker) {
        this.mode = modes.avatarPicker
      }
    },
    dragOverHandler: function (event, overTarget) {
      if (!overTarget) {
        event.dataTransfer.dropEffect = 'none'
      }
    },
    dropHandler: function (event, data, overTarget) {
      if (overTarget) {
        if (data.uri) {
          this.performDrop({ mode: data.mode, data: data.uri, event })
        }

        if (data.file) {
          this.performDrop({ mode: data.mode, data: data.file, event })
        }
      }
    },
    performDrop: function (drop) {
      this.mode = drop.mode
      if (drop.mode === modes.link) {
        this.link = drop.data
      } else if (drop.mode === modes.iframe) {
        this.iframe = drop.data
      } else if (drop.mode === modes.file) {
        this.checkFile(drop.data)
      } else if (drop.mode === modes.data) {
        this.dataURI = drop.data
      }
      if (this.error === '') {
        this.attach()
      }
    },
    selectImage: function (url) {
      this.mode = modes.link
      this.link = url
    },
    attach: function () {
      if (this.formSelected) {
        this.formSelected = false
      } else {
        if (this.canAttach) {
          this.attaching = true

          const payload = {
            file: this.mode === modes.file ? this.file : null,
            url: this.mode === modes.link ? this.link : null,
            data: this.mode === modes.data ? this.dataURI : null,
            poll: this.mode === modes.poll ? this.poll : null,
            iframe: this.mode === modes.iframe ? this.iframe : null
          }
          switch (this.type) {
            case types.image:
              return this.attachImage(payload)
            case types.link:
              return this.attachLink(payload)
            case types.file:
              return this.attachFile(payload)
            case types.mobileUpload:
              return this.attachMobileUpload(payload)
            case types.poll:
              return this.attachPoll(payload)
            case types.imageorvideo:
              return this.attachImageOrVideo(payload)
            case types.iframe:
              return this.attachIframe(payload)
          }
          this.error = defaultError
        }
      }
    },
    attachImageOrVideo: function (payload) {
      if (payload.url && video.isVideo(payload.url)) {
        this.attachLink(payload)
      } else if (payload.url || this.isImage(payload.file.type) || payload.data) {
        this.attachImage(payload)
      } else {
        this.attachVideo(payload)
      }
    },
    attachImage: function (payload) {
      this.attachment = { type: types.image, url: '', metadata: { isDownloadable: this.isDownloadable } }
      if (payload.url) {
        if (!urlLib.isValid(payload.url)) {
          this.error = 'Image URL is not in a valid format'
          this.attaching = false
          return
        }

        return this.getHeader(payload.url).then((header) => {
          if (header.contentType.substring(0, 5) === 'image') {
            this.attaching = false
            this.attachment.url = payload.url
            this.attachment.metadata.type = header.contentType
            this.close(true)
          } else {
            this.error = 'Image not found at URL'
            this.attaching = false
          }
        }, () => {
          this.error = 'Unable to load image'
          this.attaching = false
        })
      }

      if (payload.data) {
        this.attachment.url = payload.data
        return this.close(true)
      }

      if (payload.file) {
        this.attachment.metadata.type = payload.file.type
      } else {
        this.attaching = false
        return
      }

      const reader = new FileReader()

      const onSuccess = () => {
        this.attaching = false
        this.attachment.url = reader.result
        this.attachment.file = this.file
        this.close(true)
      }

      const onFail = () => {
        this.attaching = false
        this.file = ''
        this.error = 'Unable to read image URL'
      }

      // cordova work-around; do not have addEventListener
      if (reader.addEventListener) {
        reader.addEventListener('load', onSuccess, onFail)
      } else {
        reader.onload = onSuccess
        reader.onerror = onFail
      }
      this.isDownloadable = false // to reset the value of checkbox of isDownloadable
      reader.readAsDataURL(this.file)
    },
    attachLink: function (payload) {
      if (!payload.url) {
        this.attaching = false
        this.error = 'Unable to attach file as link'
        return
      }

      this.displayAttachingMessages()

      if (payload.url.substring(0, 4) !== 'http' && payload.url.substring(0, 1) !== '/') {
        payload.url = `http://${payload.url}`
      }

      const isProjectOrStoryURL = /https?:\/\/(www.)?(makeprojects.com\/||engineering.com\/||pbworld.stagepb.com)?(ysc\/||de\/||isef\/||\/ajas\/)?(project||story)/.test(payload.url)

      if (isProjectOrStoryURL) {
        this.setToShareURL(payload)
      }

      const url = urlLib.fix(payload.url)

      if (!urlLib.isValid(url)) {
        this.error = 'Link URL is not in a valid format'
        this.resetAttachingMessages()
        this.attaching = false
        return
      }

      this.getLinkDetails(url).then((details) => {
        const type = video.isVideo(url) ? types.embeddedVideo : types.link
        this.resetAttachingMessages()
        this.attaching = false
        this.attachment = { type, url, metadata: details }
        this.close(true)
      }, () => {
        this.error = 'Unable to attach link'
        this.resetAttachingMessages()
        this.attaching = false
      })
    },
    attachIframe: function (payload) {
      if (!payload.iframe) {
        this.attaching = false
        this.error = 'Unable to attach file as link'
        return
      }
      this.displayAttachingMessages()

      if (payload.iframe.substring(0, 4) !== 'http' && payload.iframe.substring(0, 1) !== '/') {
        payload.iframe = `http://${payload.iframe}`
      }
      const url = urlLib.fix(payload.iframe)

      if (!urlLib.isValid(url)) {
        this.error = 'Link URL is not in a valid format'
        this.resetAttachingMessages()
        this.attaching = false
        return
      }

      this.getIframeDetails(url).then((details) => {
        const type = video.isVideo(url) ? types.embeddedVideo : types.iframe
        this.resetAttachingMessages()
        this.attaching = false
        this.attachment = { type, url, metadata: details }
        this.close(true)
      }, () => {
        this.error = 'Unable to attach link'
        this.resetAttachingMessages()
        this.attaching = false
      })
    },
    attachPoll: function (payload) {
      this.error = ''
      if (this.poll.question === '') {
        this.error = 'You must add a question to create poll'
        this.attaching = false
        return
      }
      if (this.pollOptions.length < 2 && this.pollOptions === [] && !this.textField) {
        this.error = 'You must have at least 2 options to save a poll'
        this.attaching = false
        return
      }
      this.mode = modes.poll
      this.attaching = false

      let questionType = 'radio'
      if (this.multiSelect) {
        questionType = 'multi'
      } else if (this.textField) {
        questionType = 'text'
        this.pollOptions = [{
          ...this.newPollOption
        }]
      }
      this.attachment = {
        type: 'poll',
        question: this.poll.question,
        pollOptions: this.pollOptions,
        openDate: moment(this.dateToday).format('YYYY-MM-DD'),
        closeDate: moment(this.endDate).format('YYYY-MM-DD'),
        polltype: this.poll.selectedType || 'publicproject',
        questionType: questionType
      }
      this.close(true)
    },
    attachFile: function (payload) {
      if (payload.url) {
        if (!urlLib.isValid(payload.url)) {
          this.error = 'File URL is not in a valid format'
          this.attaching = false
          return
        }

        this.getHeader(payload.url).then((header) => {
          this.attaching = false
          const metadata = { name: header.fileName, size: header.contentLength, type: header.contentType, isDownloadable: true } // We need a file attachment to be downloadable
          this.attachment = { type: types.file, url: payload.url, metadata }
          this.close(true)
        }, () => {
          this.error = 'Unable to read file URL'
          this.attaching = false
        })
      }

      if (payload.file) {
        this.attaching = false
        const metadata = { name: payload.file.name, size: payload.file.size, type: payload.file.type, isDownloadable: true } // We need a file attachment to be downloadable
        this.attachment = { type: types.file, url: '', metadata, file: payload.file }
        this.close(true)
      }
    },
    attachVideo: function (payload) {
      if (payload.file) {
        const transactionId = this.transactionId()
        // mediainfo.js requires a WASM dependency to work
        const opts = {
          chunkSize: 1024 * 1024,
          format: 'object',
          locateFile: (path, prefix) => 'https://firebasestorage.googleapis.com/v0/b/project-leo-mvp.appspot.com/o/wasm%2FMediaInfoModule.wasm?alt=media&token=9c16cbeb-a7c2-43c1-a41c-f0d298cb3676'
        }
        const getSize = () => payload.file.size

        const readChunk = (chunkSize, offset) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (event) => {
              if (event.target.error) {
                reject(event.target.error)
              }
              resolve(new Uint8Array(event.target.result))
            }
            reader.readAsArrayBuffer(payload.file.slice(offset, offset + chunkSize))
          })

        // eslint-disable-next-line no-undef
        MediaInfo(opts, (mi) => {
          mi.analyzeData(getSize, readChunk)
            .then(({ media }) => {
              const { Width, Height } = media.track[1]
              upload.video(payload.file, transactionId, Width, Height, (i) => {
                this.reportProgress(i, 'Uploading')
              }).then(res => {
                const metadata = {
                  name: payload.file.name,
                  size: payload.file.size,
                  type: payload.file.type,
                  hls_url: res.data.hls_url,
                  dash_url: res.data.dash_url,
                  direct_url: res.data.direct_url,
                  transactionId: transactionId,
                  isDownloadable: this.isDownloadable
                }
                this.attachment = { type: types.video, url: res.data.thumbnail_url, metadata }

                this.attaching = false
                this.close(true)
              })
            })
            .catch((error) => {
              console.error(`An error occured:\n${error.stack}`)
            })
        }, (err) => {
          console.error(err)
        })
      } else {
        this.error = 'Unable to attach video'
        this.attaching = false
      }
    },
    attachMobileUpload: function (payload) {
      if (payload.url) {
        if (payload.url.substring(0, 4) !== 'http' && payload.url.substring(0, 1) !== '/') {
          payload.url = `http://${payload.url}`
        }

        const url = urlLib.fix(payload.url)

        if (!urlLib.isValid(url)) {
          this.error = 'Attachment URL is not in a valid format'
          this.attaching = false
          return
        }

        this.getHeader(url).then((header) => {
          if (header.contentType === 'text/html') {
            return this.attachLink(payload)
          } else {
            return this.attachFile(payload)
          }
        }, () => {
          this.error = 'Unable to attachment URL'
          this.attaching = false
        })
      } else {
        this.error = defaultError
        this.attaching = false
      }
    },
    reportProgress: function (progress, task, description) {
      this.progress = {
        percentage: progress,
        description: description,
        task: task
      }
    },
    transactionId: function () {
      const validChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let array = new Uint8Array(40)
      window.crypto.getRandomValues(array)
      array = array.map(x => validChars.charCodeAt(x % validChars.length))
      return String.fromCharCode.apply(null, array)
    },
    getHeader: function (url) {
      return new Promise((resolve, reject) => {
        upload.header(url).then((response) => {
          resolve(response.data.result.header)
        }, (error) => {
          reject(error)
        })
      })
    },
    getLinkDetails: function (url) {
      return new Promise((resolve, reject) => {
        upload.linkV3(url).then((response) => {
          const details = response.data.result.details
          if (details.screenshot) { // embed the download link for screenshots
            firebase.storage().ref(details.screenshot).getDownloadURL().then((url) => {
              details.key = details.screenshot.split('/').pop()
              details.screenshot = url
              resolve(details)
            }, () => {
              details.screenshot = ''
              resolve(details)
            })
          } else {
            resolve(details)
          }
        }, (error) => {
          reject(error)
        })
      })
    },
    getIframeDetails: function (url) {
      return new Promise((resolve, reject) => {
        upload.iframeV3(url).then((response) => {
          const details = response.data.result.details
          if (details.screenshot) { // embed the download link for screenshots
            firebase.storage().ref(details.screenshot).getDownloadURL().then((url) => {
              details.key = details.screenshot.split('/').pop()
              details.screenshot = url
              resolve(details)
            }, () => {
              details.screenshot = ''
              resolve(details)
            })
          } else {
            resolve(details)
          }
        }, (error) => {
          reject(error)
        })
      })
    },
    addPollOption: function () {
      this.pollOptions.push(Object.assign({}, this.newPollOption))
    },
    deletePollOption: function (index) {
      // Need to add poll options
      this.pollOptions.splice(index, 1)
    },
    isVideo: function (file) {
      return this.videoFileTypes.includes(file)
    },
    isImage: function (file) {
      return this.imageFileTypes.includes(file)
    },
    setToShareURL: function (payload) {
      const platforms = ['makeprojects', 'engineering', 'projectboard.world', 'pbworld.stagepb']

      platforms.forEach(platform => {
        const regex = new RegExp('https?://(www.)?' + platform + '.com(/ysc||/de||/isef||/ajas)?')
        payload.url = payload.url.replace(regex, match => {
          return `${match}/share`
        })
        return payload.url
      })
    },
    displayAttachingMessages: function () {
      let i = 1
      const length = i18n.t('projectPage.attaching').length

      this.timerId = setInterval(() => {
        this.attachingMessage = i18n.t(`projectPage.attaching[${i++ % length}]`)
      }, 3 * 1000)
    },
    resetAttachingMessages: function () {
      clearInterval(this.timerId)
      this.attachingMessage = i18n.t('projectPage.attaching[0]')
    }
  }

}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.avatar-close-button {
  position: absolute;
  right: 0;
}
.avatar-container {
  padding-top: 1rem;
  text-align: center;
}
input[type="file"] {
  display: none;
}

.checkboxes {
  display: inline-block;
  padding-top: 0.3rem;
}

.poll {
  .title {
    font-size: .9rem;
    margin: 1rem 0;
  }

  .option {
    margin-top: 1rem;
  }

  .option:last-of-type {
    margin: 1rem 0;
  }

  .button {
    border-radius: 1px;
  }

  .delete {
    background-color: $primary;
    opacity: .7;
    margin-top: .5rem;

    &:hover {
      opacity: 1;
    }
  }
}

.modal-card {
  padding: 2rem;
  background-color: white;
  max-width: 600px;
}

.modal-close {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  background-color: white;
  color: $grey-light;
}

.modal-close::before, .modal-close::after {
  background-color: $grey-light;
}

.drop-target {
  border: dashed 3px darkgray;
  padding: 1.5rem;
  text-align: center;
  font-size: 2rem;

  &.over {
    background: lightgray;
  }
}

.file-input-container {
  overflow: hidden;
}

.modal-card-body {
  padding: 20px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.modal-header {
  font-size: .9rem;
  padding-bottom: 20px;
  margin-bottom: 0;
  border-bottom: 1px solid $grey-lighter;
}

.modal-header:before {
  content: "";
  background: $primary;
  position: absolute;
  top: 68px;
  height: 1px;
  width: 87px;
}

.loading-container {
  align-items: center;
}

.button-container {
  justify-content: flex-end;
  &_form {
    justify-content: space-between !important;
  }
  &_icon {
    margin-right: 5px; margin-top: 5px;
    color: $primary;
  }
  &_text {
    font-size: .8em;
    max-width: 150px;
  }
}

.button:active,
.button:focus,
.button:focus-within {
  border-color: $primary;
  -webkit-box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
  box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
}

.cancel-and-save {
  div {
    margin-left: .5rem;
    border-radius: 1px;
    margin-top: 20px;
    font-size: .65rem;
    min-width: 72px;
    height: 28px;
  }
}
.notification.is-danger {
  white-space: pre-line;
}
.remove-outline {
  outline: none;
}
</style>
